import { useTranslation } from "react-i18next"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { BetterDocExchangeLink } from "@/modules/admin/better-doc/components/better-doc-exchange-link"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { EXCHANGE_OUTCOME_TAG_VARIANTS } from "@/modules/admin/better-doc/lib/better-doc-tag-variants"
import { formatBetterDocDateTime, formatBetterDocValue } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocExchangeSummary } from "@/modules/admin/better-doc/services/betterDocSessionQuery"

type BetterDocExchangeTableProps = {
  exchanges: ReadonlyArray<BetterDocExchangeSummary>
  emptyMessage: string
  testId: string
}

const HTTP_ERROR_STATUS_CODE_MINIMUM = 300

export const BetterDocExchangeTable = ({ exchanges, emptyMessage, testId }: BetterDocExchangeTableProps) => {
  const { t } = useTranslation()

  if (exchanges.length === 0) {
    return (
      <p className="text-on-surface-subdued text-sm" data-testid={`${testId}-empty`}>
        {emptyMessage}
      </p>
    )
  }

  return (
    <BetterDocTable data-testid={testId}>
      <thead>
        <tr>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.exchange")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.startedAt")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.operation")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.createdBy")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.request")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.status")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.outcome")}</BetterDocTableHeaderCell>
          <BetterDocTableHeaderCell>{t("admin.betterDoc.exchangeTable.duration")}</BetterDocTableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {exchanges.map((exchange) => (
          <tr
            key={exchange.exchangeId}
            className="hover:bg-surface-hovered"
            data-testid="better-doc-admin-exchange-row">
            <BetterDocTableCell>
              <div className="flex flex-wrap items-center gap-1">
                <BetterDocExchangeLink exchangeId={exchange.exchangeId} />
                {exchange.isTestCase ? (
                  <Tag type="attention" hasIcon={false} label={t("admin.betterDoc.testCaseTag")} />
                ) : null}
              </div>
            </BetterDocTableCell>
            <BetterDocTableCell className="whitespace-nowrap">
              {formatBetterDocDateTime(exchange.startedAt)}
            </BetterDocTableCell>
            <BetterDocTableCell>{getBetterDocEnumLabel(t, "exchangeOperation", exchange.operation)}</BetterDocTableCell>
            <BetterDocTableCell>{exchange.createdBy}</BetterDocTableCell>
            <BetterDocTableCell className="max-w-xs">
              <span className="block truncate" title={exchange.requestUri}>
                {`${exchange.httpMethod} ${exchange.requestUri}`}
              </span>
            </BetterDocTableCell>
            <BetterDocTableCell
              className={cn(
                exchange.responseStatusCode !== null &&
                  exchange.responseStatusCode >= HTTP_ERROR_STATUS_CODE_MINIMUM &&
                  "text-critical",
              )}>
              {formatBetterDocValue(exchange.responseStatusCode)}
            </BetterDocTableCell>
            <BetterDocTableCell>
              <div className="flex flex-wrap items-center gap-1">
                <Tag
                  type={EXCHANGE_OUTCOME_TAG_VARIANTS[exchange.outcome]}
                  label={getBetterDocEnumLabel(t, "exchangeOutcome", exchange.outcome)}
                />
                {exchange.hasUnexpectedResponse ? (
                  <Tag type="critical" label={t("admin.betterDoc.exchangeTable.unexpectedResponse")} />
                ) : null}
                {exchange.failureKind === null ? null : (
                  <span className="text-on-surface-subdued text-xs">{exchange.failureKind}</span>
                )}
              </div>
            </BetterDocTableCell>
            <BetterDocTableCell className="whitespace-nowrap">
              {formatBetterDocValue(exchange.durationMilliseconds)}
            </BetterDocTableCell>
          </tr>
        ))}
      </tbody>
    </BetterDocTable>
  )
}
